import React, { useEffect, useState } from 'react';
import { useSEO } from '../hooks/useSEO';
import { LANDING_PAGES, SERVICES_FOR_LINKS, BOROUGHS_FOR_LINKS } from '../data/landingPages';
import { trackLead } from '../lib/tracking';

const SITE_ORIGIN = 'https://exterminatorproservice.com';

const buildJsonLd = (page) => {
  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_ORIGIN}/` },
      { '@type': 'ListItem', position: 2, name: page.h1, item: `${SITE_ORIGIN}/${page.slug}` },
    ],
  };

  const faqSchema = page.faqs && page.faqs.length ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: page.faqs.map(f => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a }
    }))
  } : null;

  const pageTypeSchema = {
    '@context': 'https://schema.org',
    '@type': page.type === 'service' ? 'Service' : 'LocalBusiness',
    name: page.h1,
    description: page.metaDescription,
    url: `${SITE_ORIGIN}/${page.slug}`,
    telephone: '+1-646-209-9955',
    ...(page.type === 'borough' ? {
      areaServed: { '@type': 'City', name: `${page.name}, NY` },
      address: {
        '@type': 'PostalAddress',
        streetAddress: '2010 Seagirt Blvd',
        addressLocality: 'Queens',
        addressRegion: 'NY',
        postalCode: '11691',
        addressCountry: 'US'
      },
    } : {
      serviceType: page.service,
      provider: {
        '@type': 'LocalBusiness',
        name: 'Exterminator ProService Connect',
        telephone: '+1-646-209-9955',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '2010 Seagirt Blvd',
          addressLocality: 'Queens',
          addressRegion: 'NY',
          postalCode: '11691',
          addressCountry: 'US'
        }
      },
      areaServed: { '@type': 'City', name: 'New York City' },
    })
  };

  const blocks = [breadcrumb, pageTypeSchema];
  if (faqSchema) blocks.push(faqSchema);
  return blocks;
};

export const LandingPage = ({ slug, onRequestService }) => {
  const page = LANDING_PAGES.find(p => p.slug === slug);
  const [showStickyBar, setShowStickyBar] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setShowStickyBar(window.scrollY > 400);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useSEO({
    title: page ? page.title : 'Exterminator ProService Connect',
    description: page ? page.metaDescription : '',
    canonicalPath: page ? `/${page.slug}` : '/',
    jsonLd: page ? buildJsonLd(page) : null,
  });

  if (!page) {
    return (
      <div className="bg-white p-10 rounded-xl shadow-2xl max-w-2xl mx-auto text-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-3">Page not found</h1>
        <a href="/" className="text-indigo-600 hover:underline">← Back to Home</a>
      </div>
    );
  }

  return (
    <article className="bg-white p-6 md:p-10 rounded-xl shadow-2xl border-t-8 border-indigo-600 pb-24 md:pb-10" data-testid={`landing-${page.slug}`}>
      <nav className="text-xs text-gray-500 mb-4" aria-label="Breadcrumb">
        <a href="/" className="hover:text-indigo-600">Home</a>
        <span className="mx-2">/</span>
        <span className="text-gray-700">{page.h1}</span>
      </nav>

      <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-3" data-testid="landing-h1">
        {page.h1}
      </h1>

      <p className="text-gray-700 text-lg mb-6 leading-relaxed">{page.intro}</p>

      <div className="flex flex-wrap gap-3 mb-8">
        <a
          href="tel:+16462099955"
          onClick={() => trackLead({ method: 'phone_click', location: 'hero' })}
          className="flex items-center px-5 py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg transition shadow-md"
          data-testid="landing-call-cta"
        >
          📞 Call (646) 209-9955
        </a>
        <button
          type="button"
          onClick={() => { trackLead({ method: 'form_open', location: 'hero' }); onRequestService(); }}
          className="px-5 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg transition shadow-md"
          data-testid="landing-form-cta"
        >
          Request Service Online
        </button>
      </div>

      {page.bullets && (
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">What you get</h2>
          <ul className="space-y-2">
            {page.bullets.map((b, i) => (
              <li key={i} className="flex items-start">
                <span className="text-indigo-600 mr-2 mt-1">●</span>
                <span className="text-gray-700">{b}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {page.neighborhoods && (
        <section className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">Neighborhoods We Serve</h2>
          <div className="flex flex-wrap gap-2">
            {page.neighborhoods.map(n => (
              <span key={n} className="px-3 py-1 bg-indigo-50 text-indigo-800 text-sm rounded-full border border-indigo-100">
                {n}
              </span>
            ))}
          </div>
        </section>
      )}

      <section className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Frequently Asked Questions</h2>
        <div className="space-y-4">
          {page.faqs.map((f, i) => (
            <details key={i} className="group bg-gray-50 rounded-lg border border-gray-200 p-4">
              <summary className="font-semibold text-gray-800 cursor-pointer flex justify-between items-center">
                {f.q}
                <span className="ml-2 text-indigo-500 group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <p className="mt-3 text-gray-700 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Internal link blocks — critical for crawl + topical authority */}
      <section className="border-t border-gray-200 pt-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-3">
          {page.type === 'borough' ? 'Popular Services' : 'Service Areas'}
        </h2>
        <div className="grid sm:grid-cols-2 gap-2">
          {(page.type === 'borough' ? SERVICES_FOR_LINKS : BOROUGHS_FOR_LINKS).map(l => (
            <a
              key={l.slug}
              href={`/${l.slug}`}
              className="text-indigo-600 hover:text-indigo-800 hover:underline text-sm"
            >
              → {l.label}
            </a>
          ))}
        </div>
      </section>

      <section className="bg-gradient-to-r from-indigo-600 to-blue-700 text-white rounded-xl p-6 text-center">
        <h2 className="text-2xl font-bold mb-2">Ready to get started?</h2>
        <p className="mb-4 opacity-90">Call us 24/7 or request service online — we respond fast.</p>
        <div className="flex flex-wrap justify-center gap-3">
          <a
            href="tel:+16462099955"
            onClick={() => trackLead({ method: 'phone_click', location: 'footer_cta' })}
            className="px-5 py-2 bg-white text-indigo-700 font-bold rounded-lg hover:bg-gray-100"
          >
            📞 (646) 209-9955
          </a>
          <button
            type="button"
            onClick={() => { trackLead({ method: 'form_open', location: 'footer_cta' }); onRequestService(); }}
            className="px-5 py-2 bg-indigo-900 text-white font-bold rounded-lg hover:bg-indigo-950"
          >
            Request Online
          </button>
        </div>
      </section>

      {/* Sticky conversion bar — appears after scrolling past the hero */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-40 transition-transform duration-300 ${showStickyBar ? 'translate-y-0' : 'translate-y-full'}`}
        data-testid="sticky-cta-bar"
        aria-hidden={!showStickyBar}
      >
        <div className="bg-white/95 backdrop-blur-md border-t border-gray-200 shadow-[0_-4px_12px_rgba(0,0,0,0.08)] px-4 py-3">
          <div className="max-w-4xl mx-auto flex items-center justify-between gap-3 flex-wrap">
            <div className="hidden sm:block">
              <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">24/7 emergency service</p>
              <p className="text-sm text-gray-800 font-medium leading-tight">Free quote · Licensed &amp; insured · Same-day</p>
            </div>
            <div className="flex-1 sm:flex-none flex gap-2 justify-end w-full sm:w-auto">
              <a
                href="tel:+16462099955"
                onClick={() => trackLead({ method: 'phone_click', location: 'sticky_bar' })}
                data-testid="sticky-call-btn"
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg shadow-md transition text-sm sm:text-base"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                </svg>
                <span className="hidden sm:inline">(646) 209-9955</span>
                <span className="sm:hidden">Call</span>
              </a>
              <button
                type="button"
                onClick={() => { trackLead({ method: 'form_open', location: 'sticky_bar' }); onRequestService(); }}
                data-testid="sticky-quote-btn"
                className="flex-1 sm:flex-none px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg shadow-md transition text-sm sm:text-base"
              >
                Get Free Quote
              </button>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};
